import { useState, useRef, useEffect, useCallback } from 'react';
import { ArrowLeft, Locate, Clock, MapPin, Home, Briefcase, Star, Loader2, ChevronDown, X } from 'lucide-react';
import { autocomplete, type AutocompleteResult } from '../../services/api';
import { useMapStore } from '../../stores/mapStore';
import { useVisibleRecentSearches } from '../../hooks/useVisibleRecentSearches';
import { pickGhanaProximity } from '../../utils/regionAvailability';
import { getCategoryIconName, getSearchResultCategoryLabel } from '../../constants/categories';
import { DynamicIcon } from '../ui/DynamicIcon';
import { resolveRecentSearchLocation } from '../../utils/recentSearchStorage';
import type { RecentSearch } from '../../types';

interface LocationSelection {
  coordinates: { lat: number; lng: number };
  name: string;
}

interface SavedPlace {
  name: string;
  coordinates: { lat: number; lng: number };
  label: string;
}

interface LocationSearchOverlayProps {
  field: 'origin' | 'destination';
  initialQuery?: string;
  userLocation?: { lat: number; lng: number } | null;
  savedPlaces?: SavedPlace[];
  onSelect: (location: LocationSelection) => void;
  onUseCurrentLocation: () => void;
  onClose: () => void;
}

export function LocationSearchOverlay({
  field,
  initialQuery = '',
  userLocation,
  savedPlaces = [],
  onSelect,
  onUseCurrentLocation,
  onClose,
}: LocationSearchOverlayProps) {
  const [query, setQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState<AutocompleteResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const { center, warnOnLocationFeatureUse } = useMapStore();
  const recentSearches = useVisibleRecentSearches();

  const homePlace = savedPlaces.find(p => p.label === 'home');
  const workPlace = savedPlaces.find(p => p.label === 'work');
  const otherSaved = savedPlaces.filter(p => p.label === 'saved');

  const runSearch = useCallback(async (q: string, off: number, append: boolean) => {
    if (q.trim().length < 2) {
      setSuggestions([]);
      setHasMore(false);
      return;
    }
    if (!append) setIsSearching(true);
    else setIsLoadingMore(true);
    warnOnLocationFeatureUse();

    try {
      const proximity = pickGhanaProximity(userLocation ?? null, center);
      const result = await autocomplete(q.trim(), {
        limit: 10,
        offset: off,
        proximity,
      });
      if (append) {
        setSuggestions(prev => [...prev, ...result.suggestions]);
      } else {
        setSuggestions(result.suggestions);
      }
      setHasMore(result.has_more);
      setOffset(off + result.suggestions.length);
    } catch {
      if (!append) setSuggestions([]);
    } finally {
      setIsSearching(false);
      setIsLoadingMore(false);
    }
  }, [userLocation, center, warnOnLocationFeatureUse]);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 80);
  }, []);

  useEffect(() => {
    if (initialQuery && initialQuery.length >= 2) {
      runSearch(initialQuery, 0, false);
    }
  }, [initialQuery, runSearch]);

  const handleInputChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length >= 2) {
      debounceRef.current = setTimeout(() => {
        setOffset(0);
        runSearch(value, 0, false);
      }, 300);
    } else {
      setSuggestions([]);
      setHasMore(false);
      setOffset(0);
    }
  };

  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore) {
      runSearch(query, offset, true);
    }
  };

  const handleSelectSuggestion = (s: AutocompleteResult) => {
    if (!s.coordinates) return;
    onSelect({
      coordinates: { lat: s.coordinates.latitude, lng: s.coordinates.longitude },
      name: s.text,
    });
  };

  const handleSelectRecent = (recent: RecentSearch) => {
    if (!recent.result?.id) return;
    void resolveRecentSearchLocation(recent.result.id).then((loc) => {
      if (loc) onSelect(loc);
    });
  };

  const handleSelectSaved = (place: SavedPlace) => {
    onSelect({ coordinates: place.coordinates, name: place.name });
  };

  const getIconName = (type: string, category?: string | null): string => {
    if (category) {
      const n = getCategoryIconName(category);
      if (n !== 'map-pin') return n;
    }
    return getCategoryIconName(type);
  };

  const label = field === 'origin' ? 'FROM' : 'TO';
  const placeholder = field === 'origin' ? 'Search starting point...' : 'Search destination...';
  const hasQuery = query.trim().length >= 2;

  return (
    <div className="fixed inset-0 bg-white dark:bg-gray-900 z-[110] flex flex-col safe-area-top">
      {/* Header with back button + input */}
      <div className="flex items-center gap-2 px-3 py-3 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 flex-shrink-0">
        <button
          onClick={onClose}
          className="p-2 -ml-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white transition-colors flex-shrink-0"
        >
          <ArrowLeft size={22} />
        </button>

        <div className="flex-1 flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-xl px-3 py-2.5">
          <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 w-8 flex-shrink-0">{label}</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={placeholder}
            autoComplete="off"
            className="flex-1 min-w-0 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-base outline-none"
          />
          {query ? (
            <button
              onClick={() => { setQuery(''); setSuggestions([]); setHasMore(false); inputRef.current?.focus(); }}
              className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
            >
              <X size={16} />
            </button>
          ) : (
            <MapPin size={16} className="text-gray-400 flex-shrink-0" />
          )}
        </div>
      </div>

      {/* Results / default state */}
      <div className="flex-1 overflow-y-auto">

        {/* Use current location — fetches GPS if needed */}
        <button
          onClick={onUseCurrentLocation}
          className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-primary-50 dark:hover:bg-primary-900/20 border-b border-gray-100 dark:border-gray-800 transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center flex-shrink-0">
            <Locate size={16} className="text-primary-600 dark:text-primary-400" />
          </div>
          <span className="text-sm font-medium text-primary-600 dark:text-primary-400">Use current location</span>
        </button>

        {/* Typing: show autocomplete results */}
        {hasQuery ? (
          <>
            {isSearching && suggestions.length === 0 && (
              <div className="flex items-center gap-3 px-4 py-3.5 text-gray-400 dark:text-gray-500">
                <Loader2 size={16} className="animate-spin flex-shrink-0" />
                <span className="text-sm">Searching...</span>
              </div>
            )}

            {!isSearching && suggestions.length === 0 && (
              <div className="px-4 py-8 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <MapPin size={22} className="text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No results found</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Try a different search term</p>
              </div>
            )}

            {suggestions.map((s, i) => (
              <button
                key={`${s.id || s.text}-${i}`}
                onClick={() => handleSelectSuggestion(s)}
                disabled={!s.coordinates}
                className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800 border-b border-gray-100 dark:border-gray-800 transition-colors text-left disabled:opacity-50"
              >
                <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <DynamicIcon name={getIconName(s.type, s.category)} size={15} className="text-gray-500 dark:text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{s.text}</p>
                  {(s.address || s.district) && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
                      {[s.address, s.district].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {(s.category || s.type === 'street') && (
                    <p className="text-xs text-primary-500 dark:text-primary-400 mt-0.5">
                      {getSearchResultCategoryLabel(s.type, s.category)}
                    </p>
                  )}
                </div>
                {s.distance_meters != null && (
                  <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                    {s.distance_meters < 1000
                      ? `${Math.round(s.distance_meters)}m`
                      : `${(s.distance_meters / 1000).toFixed(1)}km`}
                  </span>
                )}
              </button>
            ))}

            {hasMore && (
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="w-full flex items-center justify-center gap-2 px-4 py-3.5 text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-800 border-t border-gray-100 dark:border-gray-700 transition-colors disabled:opacity-60"
              >
                {isLoadingMore ? (
                  <><Loader2 size={14} className="animate-spin" /> Loading...</>
                ) : (
                  <><ChevronDown size={14} /> Show more results</>
                )}
              </button>
            )}
          </>
        ) : (
          <>
            {/* Saved places shortcuts */}
            {(homePlace || workPlace || otherSaved.length > 0) && (
              <div className="px-4 pt-4 pb-2">
                <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">Saved</p>
                <div className="space-y-1">
                  {homePlace && (
                    <button
                      onClick={() => handleSelectSaved(homePlace)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                        <Home size={15} className="text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">Home</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{homePlace.name}</p>
                      </div>
                    </button>
                  )}
                  {workPlace && (
                    <button
                      onClick={() => handleSelectSaved(workPlace)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center flex-shrink-0">
                        <Briefcase size={15} className="text-orange-600 dark:text-orange-400" />
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">Work</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{workPlace.name}</p>
                      </div>
                    </button>
                  )}
                  {otherSaved.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => handleSelectSaved(p)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center flex-shrink-0">
                        <Star size={15} className="text-purple-600 dark:text-purple-400" />
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{p.name}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Recent searches */}
            {recentSearches.length > 0 && (
              <div className="px-4 pt-4 pb-4">
                <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">Recent</p>
                <div className="space-y-1">
                  {recentSearches.slice(0, 6).map((recent) => (
                    <button
                      key={recent.id}
                      onClick={() => handleSelectRecent(recent)}
                      disabled={!recent.result}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-40"
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                        <Clock size={15} className="text-gray-400 dark:text-gray-500" />
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                          {recent.query}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!homePlace && !workPlace && otherSaved.length === 0 && recentSearches.length === 0 && (
              <div className="px-4 py-10 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <MapPin size={22} className="text-gray-400" />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Start typing to search</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
